import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from './LoadingSpinner';
import { CreateCaseRequest, ImageType } from '../types';
import { MEDICAL_TERMINOLOGY } from '../constants/medicalTerminology';
import { AREAS_OF_INTEREST } from '../../../shared/constants/index.js';
import { useEmailVerificationGuard } from '../hooks/useEmailVerification';
import { EmailVerificationOverlay } from './EmailVerificationOverlay';

interface CaseFormData extends CreateCaseRequest {
  images: File[];
  imageTypes: ImageType[];
  imageStaining: string[];
  imageMagnification: string[];
  urgencyLevel: 'low' | 'normal' | 'high' | 'urgent';
  isAnonymous: boolean;
}

interface UploadProgress {
  uploading: boolean;
  progress: number;
  currentStep: string;
  timeElapsed: number;
}

const MEDICAL_SYSTEMS = AREAS_OF_INTEREST;


const URGENCY_LEVELS = [
  { value: 'normal', label: MEDICAL_TERMINOLOGY.URGENCY_LEVELS.NORMAL },
  { value: 'low', label: MEDICAL_TERMINOLOGY.URGENCY_LEVELS.LOW },
  { value: 'high', label: MEDICAL_TERMINOLOGY.URGENCY_LEVELS.HIGH },
  { value: 'urgent', label: MEDICAL_TERMINOLOGY.URGENCY_LEVELS.URGENT }
];

const IMAGE_TYPES: ImageType[] = [
  'he',
  'histochemical', 
  'immunohistochemistry',
  'molecular',
  'gross'
];

const IMAGE_TYPE_LABELS: Record<ImageType, string> = {
  'he': 'Hematoxylin/Eosin (H&E)',
  'histochemical': 'Ιστοχημικές χρώσεις',
  'immunohistochemistry': 'Ανοσοϊστοχημεία',
  'molecular': 'Μοριακή',
  'gross': 'Μακροσκοπική'
};

const HISTOCHEMICAL_STAINS = [
  'Periodic Acid-Schiff (PAS)',
  'Alcian Blue',
  "Masson's Trichrome",
  'Reticulin Stain (Gomori)',
  'Ziehl-Neelsen (AFB)',
  'Giemsa',
  'Congo Red',
  'Oil Red O / Sudan Stains',
  "Grocott's Methenamine Silver (GMS)",
  'Gram Stain',
  'Warthin-Starry'
];



export function CaseUploadWizard() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { checkEmailVerification, requiresVerification } = useEmailVerificationGuard();
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showVerificationOverlay, setShowVerificationOverlay] = useState(false);
  
  const [formData, setFormData] = useState<CaseFormData>({
    title: '',
    description: '',
    patientAge: undefined,
    patientGender: undefined,
    clinicalHistory: '',
    diagnosis: '',
    differentialDiagnosis: '',
    caseType: '',
    tissueType: '',
    urgencyLevel: 'normal',
    macroscopicDescription: '',
    microscopicDescription: '',
    immunohistochemistryResults: '',
    specialStains: '',
    molecularMarkers: '',
    grossPathologyNotes: '',
    clinicalCorrelation: '',
    finalDiagnosis: '',
    prognosisNotes: '',
    treatmentRecommendations: '',
    images: [],
    imageTypes: [],
    imageStaining: [],
    imageMagnification: [],
    isAnonymous: false
  });

  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    uploading: false,
    progress: 0,
    currentStep: '',
    timeElapsed: 0
  });

  // Update overlay state when verification status changes
  React.useEffect(() => {
    setShowVerificationOverlay(requiresVerification);
  }, [requiresVerification]);

  // Auto-save draft every 30 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (formData.title || formData.description) {
        localStorage.setItem('case-draft', JSON.stringify(formData));
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [formData]);

  // Load draft on mount
  React.useEffect(() => {
    const draft = localStorage.getItem('case-draft');
    if (draft) {
      try {
        const parsedDraft = JSON.parse(draft);
        setFormData(prev => ({ ...prev, ...parsedDraft, images: [], imageTypes: [], imageStaining: [], imageMagnification: [] }));
      } catch (e) {
        console.error('Failed to load draft:', e);
      }
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Clear descriptions error when either description field is modified
    if ((name === 'macroscopicDescription' || name === 'microscopicDescription') && errors.descriptions) {
      setErrors(prev => ({ ...prev, descriptions: '' }));
    }
  };

  const handleImageUpload = useCallback((files: FileList) => {
    const newFiles = Array.from(files).slice(0, 5 - formData.images.length); // Max 5 images
    const validFiles = newFiles.filter(file => {
      if (!file.type.startsWith('image/')) {
        return false;
      }
      if (file.size > 100 * 1024 * 1024) { // 100MB limit
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...validFiles],
        imageTypes: [...prev.imageTypes, ...validFiles.map(() => 'he' as ImageType)],
        imageStaining: [...prev.imageStaining, ...validFiles.map(() => '')],
        imageMagnification: [...prev.imageMagnification, ...validFiles.map(() => '')]
      }));
    }
  }, [formData.images.length]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleImageUpload(files);
    }
  }, [handleImageUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      imageTypes: prev.imageTypes.filter((_, i) => i !== index),
      imageStaining: prev.imageStaining.filter((_, i) => i !== index),
      imageMagnification: prev.imageMagnification.filter((_, i) => i !== index)
    }));
  };

  const updateImageType = (index: number, type: ImageType) => {
    setFormData(prev => ({
      ...prev,
      imageTypes: prev.imageTypes.map((t, i) => i === index ? type : t),
      imageStaining: prev.imageStaining.map((s, i) => i === index ? 
        (type === 'immunohistochemistry' || type === 'histochemical' ? s : '') : s
      )
    }));
  };

  const updateImageStaining = (index: number, staining: string) => {
    setFormData(prev => ({
      ...prev,
      imageStaining: prev.imageStaining.map((s, i) => i === index ? staining : s)
    }));
  };

  const updateImageMagnification = (index: number, magnification: string) => {
    setFormData(prev => ({
      ...prev,
      imageMagnification: prev.imageMagnification.map((m, i) => i === index ? magnification : m)
    }));
  };

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Ο τίτλος είναι υποχρεωτικός';
    } else if (formData.title.length < 5) {
      newErrors.title = 'Ο τίτλος πρέπει να έχει τουλάχιστον 5 χαρακτήρες';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Η περιγραφή είναι υποχρεωτική';
    } else if (formData.description.length < 20) {
      newErrors.description = 'Η περιγραφή πρέπει να έχει τουλάχιστον 20 χαρακτήρες';
    }

    if (!formData.caseType) {
      newErrors.caseType = 'Το σύστημα είναι υποχρεωτικό';
    }

    if (!formData.tissueType || !formData.tissueType.trim()) {
      newErrors.tissueType = 'Η εντόπιση βλάβης είναι υποχρεωτική';
    }

    if (!formData.patientAge || formData.patientAge <= 0) {
      newErrors.patientAge = 'Η ηλικία του ασθενούς είναι υποχρεωτική';
    } else if (formData.patientAge > 120) {
      newErrors.patientAge = 'Η ηλικία δεν μπορεί να υπερβαίνει τα 120 έτη';
    }

    if (!formData.patientGender) {
      newErrors.patientGender = 'Το φύλο του ασθενούς είναι υποχρεωτικό';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.images.length === 0) {
      newErrors.images = 'Τουλάχιστον μία εικόνα είναι υποχρεωτική';
    }

    // Macroscopic and microscopic descriptions are now optional
    // No validation required for these fields

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    // Clear errors when navigating backwards
    setErrors({});
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  const handleSubmit = async () => {
    if (!validateStep2()) {
      return;
    }

    // Email verification is handled by the overlay blocking access

    setUploadProgress({ 
      uploading: true, 
      progress: 0, 
      currentStep: 'Προετοιμασία...',
      timeElapsed: 0
    });

    const progressStartTime = Date.now();
    const progressTimer = setInterval(() => {
      setUploadProgress(prev => ({
        ...prev,
        timeElapsed: Math.floor((Date.now() - progressStartTime) / 1000)
      }));
    }, 1000);

    try {
      // Determine if we have images to upload
      const hasImages = formData.images && formData.images.length > 0;
      
      if (hasImages) {
        // Use FormData for multipart upload with images
        const formDataToSend = new FormData();
        
        // Add all text fields
        formDataToSend.append('title', formData.title);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('macroscopic_description', formData.macroscopicDescription || '');
        formDataToSend.append('microscopic_description', formData.microscopicDescription || '');
        
        if (formData.patientAge) formDataToSend.append('patient_age', formData.patientAge.toString());
        if (formData.patientGender) formDataToSend.append('patient_gender', formData.patientGender);
        if (formData.clinicalHistory) formDataToSend.append('clinical_history', formData.clinicalHistory);
        if (formData.caseType) formDataToSend.append('case_type', formData.caseType);
        if (formData.tissueType) formDataToSend.append('tissue_type', formData.tissueType);
        
        formDataToSend.append('urgency_level', (formData.urgencyLevel || 'normal').toLowerCase());
        formDataToSend.append('is_anonymous', formData.isAnonymous.toString());

        // Add images
        formData.images.forEach((file, index) => {
          formDataToSend.append('images', file);
        });
        
        // Add image metadata
        if (formData.imageTypes.length > 0) {
          formDataToSend.append('imageTypes', JSON.stringify(formData.imageTypes));
        }
        if (formData.imageStaining.length > 0) {
          formDataToSend.append('imageStaining', JSON.stringify(formData.imageStaining));
        }
        if (formData.imageMagnification.length > 0) {
          formDataToSend.append('imageMagnification', JSON.stringify(formData.imageMagnification));
        }

        console.log('Sending multipart form data with', formData.images.length, 'images');

        setUploadProgress(prev => ({ 
          ...prev, 
          progress: 20, 
          currentStep: 'Μεταφόρτωση εικόνων στο R2...' 
        }));

        const response = await fetch('/api/v1/cases', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
            // Don't set Content-Type for FormData - browser will set it with boundary
          },
          body: formDataToSend
        });

        setUploadProgress(prev => ({ 
          ...prev, 
          progress: 80, 
          currentStep: 'Επεξεργασία δεδομένων...' 
        }));

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Σφάλμα κατά τη δημιουργία του περιστατικού');
        }

        const result = await response.json();
        
        setUploadProgress(prev => ({ 
          ...prev, 
          progress: 100, 
          currentStep: 'Ολοκληρώθηκε!' 
        }));

        // Clear draft
        localStorage.removeItem('case-draft');

        // Navigate to the new case with more delay to ensure DB commit
        setTimeout(() => {
          console.log('Navigating to case:', result.data.case.id);
          navigate(`/cases/${result.data.case.id}`, {
            state: { 
              message: `Το περιστατικό δημιουργήθηκε επιτυχώς! ${result.data.case.imagesUploaded > 0 ? `Μεταφορτώθηκαν ${result.data.case.imagesUploaded} εικόνες.` : ''}` 
            }
          });
        }, 2000);

      } else {
        // Use JSON for text-only submissions (backwards compatibility)
        const submitData = {
          title: formData.title,
          description: formData.description,
          macroscopic_description: formData.macroscopicDescription,
          microscopic_description: formData.microscopicDescription,
          patient_age: formData.patientAge,
          patient_gender: formData.patientGender,
          clinical_history: formData.clinicalHistory,
          case_type: formData.caseType,
          tissue_type: formData.tissueType,
          urgency_level: (formData.urgencyLevel || 'normal').toLowerCase(),
          is_anonymous: formData.isAnonymous
        };

        console.log('Sending JSON case data (no images):', submitData);

        setUploadProgress(prev => ({ 
          ...prev, 
          progress: 30, 
          currentStep: 'Μεταφόρτωση δεδομένων...' 
        }));

        const response = await fetch('/api/v1/cases', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          },
          body: JSON.stringify(submitData)
        });

        setUploadProgress(prev => ({ 
          ...prev, 
          progress: 70, 
          currentStep: 'Επεξεργασία δεδομένων...' 
        }));

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Σφάλμα κατά τη δημιουργία του περιστατικού');
        }

        const result = await response.json();
        
        setUploadProgress(prev => ({ 
          ...prev, 
          progress: 100, 
          currentStep: 'Ολοκληρώθηκε!' 
        }));

        // Clear draft
        localStorage.removeItem('case-draft');

        // Navigate to the new case with more delay to ensure DB commit
        setTimeout(() => {
          console.log('Navigating to case:', result.data.case.id);
          navigate(`/cases/${result.data.case.id}`, {
            state: { message: 'Το περιστατικό δημιουργήθηκε επιτυχώς!' }
          });
        }, 2000);
      }

    } catch (error) {
      setUploadProgress({ uploading: false, progress: 0, currentStep: '', timeElapsed: 0 });
      setErrors({ general: error instanceof Error ? error.message : 'Άγνωστο σφάλμα' });
    } finally {
      clearInterval(progressTimer);
    }
  };


  if (uploadProgress.uploading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <LoadingSpinner size="lg" />
          <h3 className="text-lg font-semibold text-gray-900 mt-4">
            {uploadProgress.currentStep}
          </h3>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${uploadProgress.progress}%` }}
            />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {uploadProgress.progress}% - {uploadProgress.timeElapsed}s
          </p>
          <p className="text-xs text-gray-500 mt-4">
            Παρακαλώ μη κλείσετε την εφαρμογή...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gray-50 pb-20"> {/* Extra padding for mobile bottom nav */}
      <div className={showVerificationOverlay ? "opacity-30 pointer-events-none" : ""}>
      {/* Fixed header with progress */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <h1 className="text-lg font-semibold text-gray-900">Νέο Περιστατικό</h1>
          </div>
          
          {/* Progress bar */}
          <div className="flex items-center mb-4">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  currentStep >= step ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-500'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-1 mx-2 ${
                    currentStep > step ? 'bg-blue-600' : 'bg-gray-300'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
          
          <div className="flex justify-between text-xs text-gray-500 pb-4">
            <span>Βασικά Στοιχεία</span>
            <span>Εικόνες & Περιγραφή</span>
            <span>Επιβεβαίωση</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <div className="bg-white shadow rounded-lg p-6 space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Τίτλος Περιστατικού *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                  errors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="π.χ. Βιοψία ήπατος - Αμφιβολία για κίρρωση"
              />
              {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title}</p>}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Περιγραφή Περιστατικού *
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                value={formData.description}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Συνοπτική περιγραφή του περιστατικού και των διαγνωστικών ερωτημάτων..."
              />
              {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
            </div>

            <div>
              <label htmlFor="clinicalHistory" className="block text-sm font-medium text-gray-700 mb-1">
                Κλινικό Ιστορικό
              </label>
              <textarea
                id="clinicalHistory"
                name="clinicalHistory"
                rows={3}
                value={formData.clinicalHistory}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Κλινικές πληροφορίες και ιστορικό ασθενούς..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="caseType" className="block text-sm font-medium text-gray-700 mb-1">
                  Σύστημα *
                </label>
                <select
                  id="caseType"
                  name="caseType"
                  value={formData.caseType}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                    errors.caseType ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Επιλέξτε σύστημα</option>
                  {MEDICAL_SYSTEMS.map(system => (
                    <option key={system} value={system}>{system}</option>
                  ))}
                </select>
                {errors.caseType && <p className="text-red-600 text-sm mt-1">{errors.caseType}</p>}
              </div>

              <div>
                <label htmlFor="tissueType" className="block text-sm font-medium text-gray-700 mb-1">
                  Εντόπιση βλάβης *
                </label>
                <input
                  type="text"
                  id="tissueType"
                  name="tissueType"
                  value={formData.tissueType}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                    errors.tissueType ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Περιγράψτε την εντόπιση της βλάβης"
                />
                {errors.tissueType && <p className="text-red-600 text-sm mt-1">{errors.tissueType}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="patientAge" className="block text-sm font-medium text-gray-700 mb-1">
                  Ηλικία Ασθενούς *
                </label>
                <input
                  type="number"
                  id="patientAge"
                  name="patientAge"
                  min="1"
                  max="120"
                  value={formData.patientAge || ''}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                    errors.patientAge ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.patientAge && <p className="text-red-600 text-sm mt-1">{errors.patientAge}</p>}
              </div>

              <div>
                <label htmlFor="patientGender" className="block text-sm font-medium text-gray-700 mb-1">
                  Φύλο *
                </label>
                <select
                  id="patientGender"
                  name="patientGender"
                  value={formData.patientGender || ''}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                    errors.patientGender ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Επιλέξτε φύλο</option>
                  <option value="male">Άνδρας</option>
                  <option value="female">Γυναίκα</option>
                  <option value="other">Άλλο</option>
                </select>
                {errors.patientGender && <p className="text-red-600 text-sm mt-1">{errors.patientGender}</p>}
              </div>

              <div>
                <label htmlFor="urgencyLevel" className="block text-sm font-medium text-gray-700 mb-1">
                  Προτεραιότητα
                </label>
                <select
                  id="urgencyLevel"
                  name="urgencyLevel"
                  value={formData.urgencyLevel}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  {URGENCY_LEVELS.map(level => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="macroscopicDescription" className="block text-sm font-medium text-gray-700 mb-1">
                Μακροσκοπική Περιγραφή
              </label>
              <textarea
                id="macroscopicDescription"
                name="macroscopicDescription"
                rows={3}
                value={formData.macroscopicDescription}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                  errors.descriptions ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Περιγραφή μακροσκοπικών ευρημάτων..."
              />
            </div>

            <div>
              <label htmlFor="microscopicDescription" className="block text-sm font-medium text-gray-700 mb-1">
                Μικροσκοπική Περιγραφή
              </label>
              <textarea
                id="microscopicDescription"
                name="microscopicDescription"
                rows={4}
                value={formData.microscopicDescription}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                  errors.descriptions ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Περιγραφή μικροσκοπικών ευρημάτων..."
              />
              {errors.descriptions && <p className="text-red-600 text-sm mt-1">{errors.descriptions}</p>}
            </div>
          </div>
        )}

        {/* Step 2: Images and Detailed Description */}
        {currentStep === 2 && (
          <div className="space-y-6">
            {/* Image Upload */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Εικόνες Περιστατικού</h3>
              
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center ${
                  errors.images ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <div className="mx-auto w-12 h-12 text-gray-400 mb-4">
                  📷
                </div>
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Ανεβάστε εικόνες
                </p>
                <p className="text-gray-600 mb-4">
                  Σύρτε και αφήστε ή κάντε κλικ για επιλογή αρχείων
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => e.target.files && handleImageUpload(e.target.files)}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Επιλογή Εικόνων
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Μέγιστο 5 εικόνες, 100MB η κάθε μία
                </p>
              </div>
              
              {errors.images && <p className="text-red-600 text-sm mt-1">{errors.images}</p>}

              {/* Image previews */}
              {formData.images.length > 0 && (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {formData.images.map((file, index) => (
                    <div key={index} className="relative">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <div className="absolute top-2 right-2">
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm hover:bg-red-700 touch-manipulation"
                        >
                          ×
                        </button>
                      </div>
                      <div className="absolute bottom-2 left-2 right-2 space-y-1">
                        <select
                          value={formData.imageTypes[index]}
                          onChange={(e) => updateImageType(index, e.target.value as ImageType)}
                          className="w-full text-xs bg-white/90 border border-gray-300 rounded px-1 py-0.5"
                        >
                          {IMAGE_TYPES.map(type => (
                            <option key={type} value={type}>
                              {IMAGE_TYPE_LABELS[type]}
                            </option>
                          ))}
                        </select>
                        {formData.imageTypes[index] === 'immunohistochemistry' && (
                          <input
                            type="text"
                            value={formData.imageStaining[index] || ''}
                            onChange={(e) => updateImageStaining(index, e.target.value)}
                            placeholder="Χρώση"
                            className="w-full text-xs bg-white/90 border border-gray-300 rounded px-1 py-0.5"
                          />
                        )}
                        {formData.imageTypes[index] === 'histochemical' && (
                          <select
                            value={formData.imageStaining[index] || ''}
                            onChange={(e) => updateImageStaining(index, e.target.value)}
                            className="w-full text-sm bg-white/90 border border-gray-300 rounded px-1 py-0.5"
                          >
                            <option value="">Επιλέξτε ιστοχημική χρώση</option>
                            {HISTOCHEMICAL_STAINS.map(stain => (
                              <option key={stain} value={stain}>
                                {stain}
                              </option>
                            ))}
                          </select>
                        )}
                        <input
                          type="text"
                          value={formData.imageMagnification[index] || ''}
                          onChange={(e) => updateImageMagnification(index, e.target.value)}
                          placeholder="π.χ. χ10, χ40, χ400"
                          className="w-full text-xs bg-white/90 border border-gray-300 rounded px-1 py-0.5"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Anonymous Submission Checkbox */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="isAnonymous"
                  checked={formData.isAnonymous}
                  onChange={(e) => setFormData(prev => ({ ...prev, isAnonymous: e.target.checked }))}
                  className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div className="ml-3">
                  <label htmlFor="isAnonymous" className="text-sm font-medium text-gray-900 cursor-pointer">
                    Ανώνυμη υποβολή περιστατικού
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    Τα στοιχεία σας (όνομα, νοσοκομείο/ίδρυμα και ρόλος χρήστη) δεν θα εμφανίζονται στους άλλους χρήστες, 
                    εκτός από τους συντονιστές και διαχειριστές για λόγους συμμόρφωσης. 
                    Μόνο ο ιατρικός σας ρόλος θα είναι ορατός σε όλους.
                  </p>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Step 3: Review and Submit */}
        {currentStep === 3 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Επιβεβαίωση Περιστατικού</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900">Τίτλος</h4>
                <p className="text-gray-700">{formData.title}</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900">Περιγραφή</h4>
                <p className="text-gray-700">{formData.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900">Σύστημα</h4>
                  <p className="text-gray-700">{formData.caseType}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Εντόπιση βλάβης</h4>
                  <p className="text-gray-700">{formData.tissueType}</p>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900">Εικόνες ({formData.images.length})</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2">
                  {formData.images.map((file, index) => (
                    <img
                      key={index}
                      src={URL.createObjectURL(file)}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-16 object-cover rounded"
                    />
                  ))}
                </div>
              </div>

              {formData.isAnonymous && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <h4 className="font-medium text-gray-900">Ανώνυμη Υποβολή</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Αυτό το περιστατικό θα υποβληθεί ανώνυμα. Τα στοιχεία σας θα είναι ορατά μόνο σε συντονιστές και διαχειριστές.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-red-700 text-sm">{errors.general}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between pt-6">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handleBack}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-blue-500"
            >
              Πίσω
            </button>
          )}
          
          <div className="ml-auto">
            {currentStep < 3 ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
              >
                Επόμενο
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={uploadProgress.uploading}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 disabled:opacity-50 flex items-center space-x-2"
              >
                <span>Δημιουργία Περιστατικού</span>
                {uploadProgress.uploading && <LoadingSpinner size="sm" />}
              </button>
            )}
          </div>
        </div>

      </div>
      </div>

      {/* Email Verification Overlay */}
      {showVerificationOverlay && (
        <EmailVerificationOverlay
          actionName="ανεβάσετε νέο περιστατικό"
        />
      )}
    </div>
  );
}